// ============================================================================
// SWITZERLAND TAX CALCULATION 2025
// Quellensteuer (Withholding Tax) approach - used for most employees
// Source: 
// - Swiss Federal Tax Administration
// - Cantonal tax tables 2025
// - BSV - Bundesamt für Sozialversicherungen
// ============================================================================

import { TaxCalculationResult, CountryTaxData, DeductionBreakdown } from "./types";

/**
 * SWITZERLAND TAX CONFIG 2025
 * 
 * IMPORTANT: Switzerland uses Quellensteuer (withholding tax) for most employees.
 * The withholding tax rates are MUCH lower than the regular income tax
 * because they're applied directly to gross salary without most deductions.
 * 
 * Maximum effective tax rates (including all levels):
 * - Federal: max ~11.5%
 * - Cantonal + Municipal: varies, but combined total rarely exceeds 40%
 * - Most cantons have max rates between 20-40% total
 * 
 * Social Security (employee share):
 * - AHV/IV/EO: 5.3% (no ceiling)
 * - ALV: 1.1% up to CHF 148,200, then 0.5% solidarity
 * - BVG: ~3.5-9% depending on age (on coordinated salary only)
 * - NBU: ~0.5-2% depending on industry
 */
export const SWITZERLAND_TAX_CONFIG: CountryTaxData = {
  socialSecurityRate: 6.4,
  taxBrackets: [],
  personalAllowance: 0,
  marriedAllowance: 0,
  childAllowances: [6700, 6700, 6700],
  currency: "CHF",
};

// ============================================================================
// SOCIAL SECURITY RATES 2025
// Source: BSV - Bundesamt für Sozialversicherungen
// ============================================================================

const SS_CONFIG_2025 = {
  // AHV/IV/EO (1st Pillar) - Old-age, disability, loss of earnings
  ahvIvEo: {
    employeeRate: 5.3, // Employee share (employer also pays 5.3%)
    // NO CEILING - applies to all income
  },
  
  // ALV (Unemployment insurance)
  alv: {
    employeeRate: 1.1, // Up to ceiling
    ceiling: 148200, // CHF per year (2025)
    solidarityRate: 0.5, // On income above ceiling
  },
  
  // BVG/LPP (2nd Pillar - Occupational pension)
  bvg: {
    entryThreshold: 22050, // Minimum salary to participate
    coordinationDeduction: 25725, // Koordinationsabzug
    maxInsuredSalary: 88200, // Maximum insured salary
    maxCoordinatedSalary: 62475, // 88200 - 25725
    
    // Age-based contribution rates (total, employee pays ~50%)
    ratesByAge: [
      { minAge: 25, maxAge: 34, employeeRate: 3.5 },
      { minAge: 35, maxAge: 44, employeeRate: 5.0 },
      { minAge: 45, maxAge: 54, employeeRate: 7.5 },
      { minAge: 55, maxAge: 65, employeeRate: 9.0 },
    ],
  },
  
  // NBU (Non-occupational accident insurance)
  nbu: {
    averageRate: 0.45, // More realistic average rate (lohncomputer uses ~0.45%)
  },
  
  // KTG (Krankentaggeld - Daily sickness allowance) - optional but common
  ktg: {
    averageRate: 0.14, // ~0.14% average
  },
};

// ============================================================================
// CANTONAL TAX RATES 2025
// Based on actual effective tax rates for high incomes
// These are TOTAL rates (federal + cantonal + municipal combined)
// Source: KPMG Tax Survey, Credit Suisse, cantonal data
// ============================================================================

interface CantonTaxConfig {
  name: string;
  // Effective marginal rate for high income (CHF 500k+)
  maxEffectiveRate: number;
  // Base rate for medium income (CHF 100-200k)
  mediumIncomeRate: number;
  // Low income threshold (0% or minimal tax)
  lowIncomeThreshold: number;
}

const CANTON_TAX_CONFIG: Record<string, CantonTaxConfig> = {
  // Low-tax cantons (famous for low taxes)
  ZG: { name: "Zug", maxEffectiveRate: 22.4, mediumIncomeRate: 14, lowIncomeThreshold: 20000 },
  SZ: { name: "Schwyz", maxEffectiveRate: 21.0, mediumIncomeRate: 13, lowIncomeThreshold: 18000 },
  NW: { name: "Nidwalden", maxEffectiveRate: 21.5, mediumIncomeRate: 13, lowIncomeThreshold: 18000 },
  OW: { name: "Obwalden", maxEffectiveRate: 23.0, mediumIncomeRate: 14, lowIncomeThreshold: 18000 },
  AI: { name: "Appenzell I.", maxEffectiveRate: 23.5, mediumIncomeRate: 14, lowIncomeThreshold: 17000 },
  UR: { name: "Uri", maxEffectiveRate: 24.0, mediumIncomeRate: 15, lowIncomeThreshold: 17000 },
  
  // Medium-tax cantons
  ZH: { name: "Zürich", maxEffectiveRate: 39.8, mediumIncomeRate: 25, lowIncomeThreshold: 15000 },
  LU: { name: "Luzern", maxEffectiveRate: 31.0, mediumIncomeRate: 20, lowIncomeThreshold: 16000 },
  AG: { name: "Aargau", maxEffectiveRate: 33.5, mediumIncomeRate: 22, lowIncomeThreshold: 15000 },
  TG: { name: "Thurgau", maxEffectiveRate: 32.0, mediumIncomeRate: 21, lowIncomeThreshold: 16000 },
  SG: { name: "St. Gallen", maxEffectiveRate: 34.5, mediumIncomeRate: 23, lowIncomeThreshold: 15000 },
  GR: { name: "Graubünden", maxEffectiveRate: 32.5, mediumIncomeRate: 21, lowIncomeThreshold: 16000 },
  TI: { name: "Ticino", maxEffectiveRate: 38.0, mediumIncomeRate: 25, lowIncomeThreshold: 14000 },
  
  // Higher-tax cantons
  BE: { name: "Bern", maxEffectiveRate: 41.0, mediumIncomeRate: 28, lowIncomeThreshold: 14000 },
  BS: { name: "Basel-Stadt", maxEffectiveRate: 44.8, mediumIncomeRate: 30, lowIncomeThreshold: 13000 },
  BL: { name: "Basel-Land", maxEffectiveRate: 42.2, mediumIncomeRate: 28, lowIncomeThreshold: 14000 },
  VD: { name: "Vaud", maxEffectiveRate: 41.5, mediumIncomeRate: 28, lowIncomeThreshold: 14000 },
  GE: { name: "Genève", maxEffectiveRate: 44.8, mediumIncomeRate: 30, lowIncomeThreshold: 13000 },
  FR: { name: "Fribourg", maxEffectiveRate: 37.0, mediumIncomeRate: 25, lowIncomeThreshold: 15000 },
  NE: { name: "Neuchâtel", maxEffectiveRate: 38.1, mediumIncomeRate: 26, lowIncomeThreshold: 14000 },
  VS: { name: "Valais", maxEffectiveRate: 36.0, mediumIncomeRate: 24, lowIncomeThreshold: 15000 },
  JU: { name: "Jura", maxEffectiveRate: 39.0, mediumIncomeRate: 27, lowIncomeThreshold: 14000 },
  SO: { name: "Solothurn", maxEffectiveRate: 36.5, mediumIncomeRate: 25, lowIncomeThreshold: 15000 },
  SH: { name: "Schaffhausen", maxEffectiveRate: 34.0, mediumIncomeRate: 23, lowIncomeThreshold: 15000 },
  AR: { name: "Appenzell A.", maxEffectiveRate: 29.5, mediumIncomeRate: 19, lowIncomeThreshold: 16000 },
  GL: { name: "Glarus", maxEffectiveRate: 31.0, mediumIncomeRate: 20, lowIncomeThreshold: 16000 },
};

const DEFAULT_CANTON: CantonTaxConfig = {
  name: "Default",
  maxEffectiveRate: 35.0,
  mediumIncomeRate: 23,
  lowIncomeThreshold: 15000,
};

export interface SwitzerlandTaxParams {
  grossIncome: number;
  familyStatus: string;
  dependents: number;
  age?: number;
  canton?: string;
  hasChurchTax?: boolean;
}

/**
 * Calculate Swiss taxes for 2025
 * Uses a progressive model based on actual effective tax rates
 */
export function calculateSwitzerlandTax({
  grossIncome,
  familyStatus,
  dependents,
  age = 40,
  canton = "ZH",
  hasChurchTax = false,
}: SwitzerlandTaxParams): TaxCalculationResult {
  const cantonConfig = CANTON_TAX_CONFIG[canton] || DEFAULT_CANTON;
  const isMarried = familyStatus === "married";

  // ============================================================================
  // SOCIAL SECURITY CALCULATIONS
  // ============================================================================
  
  // AHV/IV/EO (no ceiling) - 5.3%
  const ahvContrib = (grossIncome * SS_CONFIG_2025.ahvIvEo.employeeRate) / 100;
  
  // ALV with ceiling + solidarity contribution
  let alvContrib = 0;
  if (grossIncome <= SS_CONFIG_2025.alv.ceiling) {
    alvContrib = (grossIncome * SS_CONFIG_2025.alv.employeeRate) / 100;
  } else {
    // 1.1% up to ceiling + 0.5% on excess
    alvContrib = (SS_CONFIG_2025.alv.ceiling * SS_CONFIG_2025.alv.employeeRate) / 100;
    alvContrib += ((grossIncome - SS_CONFIG_2025.alv.ceiling) * SS_CONFIG_2025.alv.solidarityRate) / 100;
  }
  
  // BVG (2nd Pillar) - only on coordinated salary, capped
  let bvgContrib = 0;
  if (grossIncome >= SS_CONFIG_2025.bvg.entryThreshold && age >= 25 && age <= 65) {
    // Coordinated salary = income - coordination deduction, but capped
    const effectiveIncome = Math.min(grossIncome, SS_CONFIG_2025.bvg.maxInsuredSalary);
    const coordinatedSalary = Math.max(0, effectiveIncome - SS_CONFIG_2025.bvg.coordinationDeduction);
    const cappedCoordinatedSalary = Math.min(coordinatedSalary, SS_CONFIG_2025.bvg.maxCoordinatedSalary);
    
    // Get age-based rate
    const ageRate = SS_CONFIG_2025.bvg.ratesByAge.find(r => age >= r.minAge && age <= r.maxAge);
    const employeeBvgRate = ageRate?.employeeRate || 5.0;
    
    bvgContrib = (cappedCoordinatedSalary * employeeBvgRate) / 100;
  }

  // NBU (accident insurance) - applies to full salary but with practical caps
  // Most NBU has an insured salary cap around CHF 148,200 (same as UVG maximum)
  const nbuInsuredSalary = Math.min(grossIncome, 148200);
  const nbuContrib = (nbuInsuredSalary * SS_CONFIG_2025.nbu.averageRate) / 100;
  
  // KTG (optional daily sickness allowance)
  const ktgContrib = (Math.min(grossIncome, 148200) * SS_CONFIG_2025.ktg.averageRate) / 100;

  const socialSecurity = ahvContrib + alvContrib + bvgContrib + nbuContrib + ktgContrib;

  // ============================================================================
  // TAX CALCULATION
  // Using progressive effective rate model
  // ============================================================================
  
  // Taxable income after SS deductions
  const taxableIncome = grossIncome - socialSecurity;
  
  // Apply family deductions
  let deductions = 0;
  if (isMarried) {
    deductions += 2700; // Federal married deduction
  }
  deductions += dependents * 6700; // Child deductions
  
  const adjustedTaxableIncome = Math.max(0, taxableIncome - deductions);
  
  // Calculate effective tax rate using smooth progression
  const effectiveRate = calculateEffectiveTaxRate(
    adjustedTaxableIncome,
    cantonConfig,
    isMarried
  );
  
  const totalTax = (adjustedTaxableIncome * effectiveRate) / 100;
  
  // Split into components based on typical Swiss breakdown:
  // Federal is typically 15-20% of total tax in high-tax cantons,
  // 25-35% in low-tax cantons
  // Calculate federal tax separately for accuracy
  const federalRate = calculateFederalTaxRate(adjustedTaxableIncome, isMarried);
  const federalTax = (adjustedTaxableIncome * federalRate) / 100;
  
  // Cantonal + Municipal = Total - Federal
  const cantonalMunicipalTax = Math.max(0, totalTax - federalTax);
  const cantonalTax = cantonalMunicipalTax * 0.63; // Cantonal ~63% of cantonal+municipal
  const municipalTax = cantonalMunicipalTax * 0.37; // Municipal ~37%
  
  const taxRate = grossIncome > 0 ? (totalTax / grossIncome) * 100 : 0;

  // ============================================================================
  // BUILD BREAKDOWN
  // ============================================================================
  
  const breakdown: DeductionBreakdown[] = [];

  if (federalTax > 0) {
    breakdown.push({
      id: "bundessteuer",
      amount: federalTax,
      rate: grossIncome > 0 ? (federalTax / grossIncome) * 100 : 0,
      type: "tax",
    });
  }

  if (cantonalTax > 0) {
    breakdown.push({
      id: "kantonssteuer",
      amount: cantonalTax,
      rate: grossIncome > 0 ? (cantonalTax / grossIncome) * 100 : 0,
      type: "tax",
    });
  }

  if (municipalTax > 0) {
    breakdown.push({
      id: "gemeindesteuer",
      amount: municipalTax,
      rate: grossIncome > 0 ? (municipalTax / grossIncome) * 100 : 0,
      type: "tax",
    });
  }

  if (ahvContrib > 0) {
    breakdown.push({
      id: "ahv",
      amount: ahvContrib,
      rate: grossIncome > 0 ? (ahvContrib / grossIncome) * 100 : 0,
      type: "social",
    });
  }

  if (alvContrib > 0) {
    breakdown.push({
      id: "alv",
      amount: alvContrib,
      rate: grossIncome > 0 ? (alvContrib / grossIncome) * 100 : 0,
      type: "social",
    });
  }

  if (bvgContrib > 0) {
    breakdown.push({
      id: "bvg",
      amount: bvgContrib,
      rate: grossIncome > 0 ? (bvgContrib / grossIncome) * 100 : 0,
      type: "social",
    });
  }

  if (nbuContrib > 0) {
    breakdown.push({
      id: "nbu",
      amount: nbuContrib,
      rate: grossIncome > 0 ? (nbuContrib / grossIncome) * 100 : 0,
      type: "social",
    });
  }

  return {
    tax: totalTax,
    taxRate,
    socialSecurity,
    allowances: deductions,
    breakdown,
  };
}

/**
 * Calculate effective tax rate using smooth progression
 * Based on actual Swiss tax data patterns
 */
function calculateEffectiveTaxRate(
  taxableIncome: number,
  cantonConfig: CantonTaxConfig,
  isMarried: boolean
): number {
  // Marriage typically reduces tax by ~10-15%
  const marriageDiscount = isMarried ? 0.88 : 1.0;
  
  // Below threshold: 0% tax
  if (taxableIncome <= cantonConfig.lowIncomeThreshold) {
    return 0;
  }
  
  // Progressive curve parameters
  const { maxEffectiveRate, mediumIncomeRate, lowIncomeThreshold } = cantonConfig;
  
  // Income thresholds for rate progression
  const lowIncome = lowIncomeThreshold;
  const mediumIncome = 150000;  // Where medium rate applies
  const highIncome = 500000;    // Where max rate kicks in
  
  let effectiveRate: number;
  
  if (taxableIncome <= mediumIncome) {
    // Progressive from 0 to medium rate
    const progress = (taxableIncome - lowIncome) / (mediumIncome - lowIncome);
    effectiveRate = mediumIncomeRate * Math.pow(progress, 0.7); // Smooth curve
  } else if (taxableIncome <= highIncome) {
    // Progressive from medium to max rate
    const progress = (taxableIncome - mediumIncome) / (highIncome - mediumIncome);
    effectiveRate = mediumIncomeRate + (maxEffectiveRate - mediumIncomeRate) * progress;
  } else {
    // Above high income: max rate (but never above 45%)
    effectiveRate = Math.min(maxEffectiveRate, 45);
  }
  
  return effectiveRate * marriageDiscount;
}

/**
 * Calculate federal tax rate separately
 * Federal tax in Switzerland has a max rate of ~11.5%
 * but effective rate is lower due to progression
 */
function calculateFederalTaxRate(taxableIncome: number, isMarried: boolean): number {
  // Federal tax effective rates (approximate)
  // These are much lower than total tax
  if (taxableIncome <= 20000) return 0;
  if (taxableIncome <= 50000) return isMarried ? 0.3 : 0.5;
  if (taxableIncome <= 100000) return isMarried ? 1.5 : 2.5;
  if (taxableIncome <= 200000) return isMarried ? 3.5 : 5.0;
  if (taxableIncome <= 500000) return isMarried ? 6.0 : 7.5;
  if (taxableIncome <= 1000000) return isMarried ? 8.0 : 9.5;
  return isMarried ? 9.5 : 11.0; // Max federal effective rate
}

/**
 * Get list of available cantons for UI
 */
export function getSwissCantons(): Array<{ code: string; name: string }> {
  return Object.entries(CANTON_TAX_CONFIG).map(([code, config]) => ({
    code,
    name: config.name,
  }));
}
